<template>
    <div :key="key">
        <div v-if="editState == 1"
             class="box-header">
            <span>
                <context-button :menu-contents-function="contextMenu"
                                :index="0" />
            </span>
            <span class="pull-right-indented">
                <span v-if="preventionPlan.modified"
                      class="modified-pill">
                    Modified
                </span>
                <button v-if="editable"
                        type="button"
                        class="btn portal-btn outline icon-button"
                        @click="editClicked">
                    <i class="icon icon-pencil" />
                </button>
            </span>
        </div>
        <div v-else-if="editable"
             class="box-header button-box-header">
            <div class="pull-right-indented">
                <button type="button"
                        class="btn portal-btn outline icon-button"
                        @click="editClicked">
                    <i class="icon icon-pencil" />
                </button>
            </div>
            <br>
        </div>
        <div class="box-body">
            <div class="plan-text">
                <view-edit ref="viewEdit"
                           v-model="preventionPlan"
                           :comparison="history"
                           :layout="layout"
                           :edit-mode-on="editOn"
                           :sending="sending"
                           @cancel-pressed="cancelPressed"
                           @update:model-value="modelUpdated" />
            </div>
        </div>
    </div>
    <load-preset ref="loadPresetDlg"
                 v-model="selectedPreset"
                 :id-number="preventionPlan.goalPlanRelapsePreventionId"
                 type="1"
                 @update:model-value="presetSelectedForLoad" />
    <save-preset ref="savePresetDlg"
                 :data="preventionPlan"
                 :id-number="preventionPlan.goalPlanRelapsePreventionId"
                 type="1" />
    <delete-preset ref="deletePresetDlg"
                   :id-number="preventionPlan.goalPlanRelapsePreventionId"
                   type="1" />
    <view-edit v-if="editBothOn"
               v-model="preventionPlan"
               :comparison="history"
               :prefill="presetVal"
               :layout="layout"
               :edit-mode-on="true"
               :sending="sending"
               @cancel-pressed="cancelBothPressed"
               @update:model-value="updateBothModel" />
</template>

<script setup>
    import { defineProps, onMounted, defineEmits, computed, ref, nextTick } from 'vue';
    import axios from 'axios';
    import ViewEdit from '../viewEdit/ViewEdit.vue';
    import ContextButton from '../ContextButton.vue';
    import LoadPreset from './LoadPreset.vue';
    import SavePreset from './SavePreset.vue';
    import DeletePreset from './DeletePreset.vue';
    import { axiosConfig, axiosError } from '../../js/AjaxUtilities.js'
    import { canEdit } from '../../js/CalcGoalPlanEditState.js';
    import { toHexColorString } from '../../js/NumberUtilities.js';

    const props = defineProps({
        modelValue: {
            type: Object,
            default: null
        },
        patientId: {
            type: Number,
            default: 0
        },
        editState: {
            type: Number,
            default: 0
        },
        componentKey: {
            type: Number,
            default: 0
        },
        flat: {
            type: Boolean,
            default: false
        },
        urlHead: {
            type: String,
            default: null
        }
    });

    const emit = defineEmits(['update:modelValue']);

    onMounted(() => {
        calcPreventionPlan();
    });

    const textBoxStyle = computed(() => calcTextBoxStyle(props.modelValue.colour));
    const layout = computed(() => calcLayout(props.modelValue, props.flat));
    const key = computed(() => `plan-${props.componentKey}`);
    const editable = computed(() => canEdit(props.editState, 2)); // anyone can edit

    const preventionPlan = ref({});
    const editOn = ref(false);
    const editBothOn = ref(false);
    const sending = ref(false);

    const selectedPreset = ref(0);
    const presetSide = ref(0);
    const presetVal = ref(null);

    const loadPresetDlg = ref(null);
    const savePresetDlg = ref(null);
    const deletePresetDlg = ref(null);
    const viewEdit = ref(null);

    const history = ref(null);

    function calcPreventionPlan() {
        preventionPlan.value = { ...props.modelValue };
    }

    function calcLayout(relapsePreventionPlan, flat) {
        let layout = {
            id: `relapse-prevention-plan-${relapsePreventionPlan.goalPlanRelapsePreventionId}`,
            editTitle: "Relapse Prevention Plan",
            editSize: "dialog-adaptive"
        };

        if (flat) {
            layout.rows = [
                {
                    style: textBoxStyle.value,
                    columns: [
                        {
                            title: relapsePreventionPlan.leftName,
                            editType: "textarea",
                            attributeName: "rightText"
                        },
                        {
                            title: relapsePreventionPlan.rightName,
                            editType: "textarea",
                            attributeName: "leftText"
                        }
                    ]
                },
            ];
        } else {
            layout.rows = [
                {
                    style: textBoxStyle.value,
                    columns: [
                        {
                            title: relapsePreventionPlan.leftName,
                            editType: "textarea",
                            attributeName: "rightText"
                        }
                    ],
                },
                {
                    columns: [], // spacer row
                },
                {
                    style: textBoxStyle.value,
                    columns: [
                        {
                            title: relapsePreventionPlan.rightName,
                            editType: "textarea",
                            attributeName: "leftText"
                        }
                    ]
                },

            ];
        }
        return layout;
    }

    function editClicked() {
        editOn.value = true;
        loadHistory();
    }

    function cancelPressed() {
        editOn.value = false;
    }


    function cancelBothPressed() {
        viewEdit.value.closeEditMode();
        editBothOn.value = false;
    }

    function modelUpdated() {
        editOn.value = false;
        updateModel();
    }

    function updateBothModel() {
        updateModel(closeBothEditMode);
    }

    function closeBothEditMode() {
        viewEdit.value.closeEditMode();
        editBothOn.value = false;
    }

    function updateModel(cleanUpFunction) {
        sending.value = true;
        let data = {
            patientId: props.patientId,
            plan: preventionPlan.value
        };

        axios
            .post(`/${props.urlHead}/UpdateRelapsePreventionPlan`, data, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    preventionPlan.value = response.data.relapsePreventionPlan;
                    emit("update:modelValue", preventionPlan.value);
                } else {
                    alert(`Failed to update relapse prevention plan: ${response.data.message}`);
                }
            })
            .catch(function (error) {
                axiosError("Update relapse prevention plan", axios, error);
            })
            .finally(function () {
                sending.value = false;
                if (cleanUpFunction) {
                    cleanUpFunction();
                }
            });
    }

    function toAlphaHexColor(colour, alphaHexValueString) {
        let baseHex = toHexColorString(colour);
        return baseHex + alphaHexValueString;
    }

    function calcTextBoxStyle(colour) {
        let lineColor = toHexColorString(colour);
        let bodyColor = toAlphaHexColor(colour, "88");

        return `background-color: ${bodyColor}; border-top: 4px solid ${lineColor};`;
    }

    function contextMenu(index) {
        return [
            {
                customClass: "icon icon-import",
                label: "Load preset",
                onClick: () => {
                    loadGoalPreset(index);
                }
            },
            {
                customClass: "icon icon-export",
                label: "Save preset",
                onClick: () => {
                    saveGoalPreset(index);
                }
            },
            {
                customClass: "icon icon-delete",
                label: "Delete preset",
                onClick: () => {
                    deleteGoalPreset(index);
                }
            },
        ];
    }

    function loadGoalPreset(index) {
        presetSide.value = index;
        loadPresetDlg.value.show();
    }

    function saveGoalPreset(index) {
        savePresetDlg.value.show();
    }

    function deleteGoalPreset(index) {
        deletePresetDlg.value.show();
    }

    function presetSelectedForLoad() {
        let presetId = selectedPreset.value;

        axios
            .post(`/GoalPlan/LoadPreset`, presetId, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    editWithPreset(response.data.preset);
                } else {
                    alert(`Failed to load preset: ${response.data.message}`);
                }
            })
            .catch(function (error) {
                axiosError("Load presets", axios, error);
            })
    }

    function loadHistory() {
        if (!preventionPlan.value.modified || props.editState != 1) {
            history.value = null;
        } else if (!history.value) {
            let data = new Object();
            data.patientId = props.patientId;
            data.id = preventionPlan.value.goalPlanRelapsePreventionId

            axios
                .post(`/GoalPlan/GetPreviousRelapsePreventionPlan`, data, axiosConfig())
                .then(function (response) {
                    if (response.data.success) {
                        history.value = response.data.relapsePreventionPlan;
                    } else {
                        alert(`Failed to load plan history: ${response.data.message}`);
                    }
                })
                .catch(function (error) {
                    axiosError("Get previous relapse prevention plan", axios, error);
                })
        }
    }

    function editWithPreset(preset) {
        //presetVal.value = preset;
        const { goalPlanId, goalPlanRelapsePreventionId } = preventionPlan.value;

        preventionPlan.value = {
            ...preventionPlan.value,
            ...preset,
            goalPlanId,
            goalPlanRelapsePreventionId
        };

        emit('update:modelValue', preventionPlan.value);
        editBothOn.value = true;
    }


</script>

<style scoped>
    .plan-text {
        min-height: 5em;
    }

    .button-box-header {
        height: 40px;
    }
</style>